import { usePageContext } from 'application/contexts/PageContext';
import { CTALink, CTALinkType, Link, LinkProps } from 'designsystem/components/1-atoms/Links';
import getConfig from 'next/config';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';

export interface LinkComponent {
	url?: string;
	route?: {
		path: string;
	};
	queryString?: string;
	title?: string;
	target?: string;
	ariaLabel?: string;
	asText?: boolean;
	isCTALink?: boolean;
	ctaLinkType?: CTALinkType;
	children?: React.ReactElement;
	style?: LinkProps['style'];
	className?: string;
	callback?: () => void;
	locale?: NextLinkProps['locale'];
	currentPage?: boolean;
	prefetch?: boolean;
}

export const LinkFeature: React.FC<LinkComponent> = ({
	url,
	route,
	queryString,
	title,
	target,
	ariaLabel,
	asText,
	isCTALink = false,
	ctaLinkType = 'arrow',
	children,
	style,
	className,
	callback,
	locale,
	currentPage,
	prefetch = false,
}) => {
	const { publicRuntimeConfig } = getConfig() ?? {};
	const { host } = usePageContext() ?? {};
	const { DOMAIN_PROTOCOL: protocol } = publicRuntimeConfig ?? {};
	let conformedUrl = url ? url : route?.path; // Umbraco CDA returns url in 'url' if external and 'route.path' if internal.
	if (queryString) {
		conformedUrl = conformedUrl + queryString;
	}
	const websiteUrl = `${protocol ? `${protocol}://` : ''}${host}`;
	const internalLink = conformedUrl?.startsWith('/');

	const handleClick = () => {
		if (conformedUrl[0] !== '#') {
			const urlObject = new URL(conformedUrl, internalLink ? websiteUrl : undefined);
			const params = new URLSearchParams(urlObject.search);
			for (const [key, value] of params.entries()) {
				if (key != 'action') return;
				if (value === 'cookieRenew' && window?.CookieConsent?.renew != undefined) window.CookieConsent.renew();
			}
		}
		callback && callback();
	};

	return conformedUrl ? (
		<NextLink href={conformedUrl} scroll={!conformedUrl.includes('#')} passHref prefetch={prefetch} locale={locale} legacyBehavior>
			{isCTALink ? (
				<CTALink
					label={title}
					type={ctaLinkType}
					color="dark"
					link={{ url: conformedUrl, target: target, name: title }}
					onClick={handleClick}
					aria-current={currentPage ? 'page' : undefined}
				/>
			) : (
				<Link
					className={className}
					style={style}
					target={target}
					asText={asText}
					ariaLabel={ariaLabel}
					onClick={handleClick}
					aria-current={currentPage ? 'page' : undefined}
				>
					{children ? children : title}
				</Link>
			)}
		</NextLink>
	) : (
		<></>
	);
};
